/**
 * SUNBAY Nexus platform credentials — house modal (WHITE header, ruby
 * underline, ruby icon tile, white body, black primary). WRITE-ONLY: every
 * field starts empty; nothing stored is ever shown back.
 */
import React, { useState } from 'react';
import { X, KeyRound } from 'lucide-react';
import { Segmented } from '../cardSetup/ReaderPicker';
import { RubyGemMark } from '../../RubyGemMark';
import { type SunbayCredForm, emptySunbayCreds, sunbayCredProblems, sunbayCredBody, DEFAULT_SUNBAY_URL } from './sunbayCreds';

const RUBY_UNDERLINE = { background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' };
const INPUT = 'min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200';

const Field: React.FC<{ label: string; error?: string | null; hint?: string; children: React.ReactElement }> = ({ label, error, hint, children }) => (
  <div className="block">
    <span className="mb-1 block text-xs font-medium text-stone-700">{label}</span>
    {children.type === 'input' ? React.cloneElement(children as React.ReactElement<any>, { 'aria-label': label }) : children}
    {error ? <span className="mt-0.5 block text-[11px] text-ruby-deep">{error}</span>
      : hint ? <span className="mt-0.5 block text-[11px] text-stone-500">{hint}</span> : null}
  </div>
);

interface Props {
  onSave: (body: ReturnType<typeof sunbayCredBody>) => Promise<void>;
  onClose: () => void;
}

export const SunbayCredentialsModal: React.FC<Props> = ({ onSave, onClose }) => {
  const [f, setF] = useState<SunbayCredForm>(emptySunbayCreds);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hostToConfirm, setHostToConfirm] = useState<string | null>(null);
  const set = (k: keyof SunbayCredForm, v: string) => { setF((x) => ({ ...x, [k]: v })); setTouched((t) => ({ ...t, [k]: true })); setHostToConfirm(null); };
  const problems = sunbayCredProblems(f);
  const show = (k: keyof SunbayCredForm) => (touched[k] ? problems[k] || null : null);

  const submit = async (confirmHost = false) => {
    setBusy(true); setError(null);
    try {
      await onSave(sunbayCredBody(f, confirmHost));
      onClose();
    } catch (e: any) {
      if (e?.code === 'UNKNOWN_SUNBAY_HOST' && !confirmHost) {
        let host = f.baseUrl; try { host = new URL(f.baseUrl).hostname; } catch { /* keep text */ }
        setHostToConfirm(host);
      } else {
        // A failed save never keeps the typed secrets in memory: they are re-entered (write-only).
        setF((x) => ({ ...x, apiKey: '', secretKey: '', webhookSecret: '' }));
        setTouched((t) => ({ ...t, apiKey: false, secretKey: false, webhookSecret: false }));
        setHostToConfirm(null);
        setError(e?.serverMessage || e?.message || 'The credentials were not saved.');
      }
    } finally { setBusy(false); }
  };

  const secret = (k: 'apiKey' | 'secretKey' | 'webhookSecret', label: string, tid: string) => (
    <Field label={label} error={show(k)} hint="Write-only — never shown again after saving.">
      <input className={`${INPUT} font-mono`} type="password" value={f[k]} onChange={(e) => set(k, e.target.value)} autoComplete="new-password" data-testid={tid} />
    </Field>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="SUNBAY platform credentials">
      <div className="w-full max-w-[760px] overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="sunbay-modal">
        <header className="flex items-center gap-3 bg-white px-5 py-3.5 text-stone-900">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ruby text-white"><KeyRound size={18} /></span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[15px] font-semibold">SUNBAY platform credentials</h2>
            <div className="truncate text-xs text-stone-500">SUNBAY Nexus · CPad Pay</div>
          </div>
          <span className="rounded-full border border-stone-300 px-2.5 py-1 text-[11px] text-stone-600">Platform default</span>
          <button type="button" aria-label="Close" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-lg text-stone-500 hover:bg-stone-100 hover:text-stone-900"><X size={18} /></button>
        </header>
        <div style={RUBY_UNDERLINE} />
        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          <div className="space-y-3">
            <Field label="App ID" error={show('appId')}>
              <input className={`${INPUT} font-mono`} value={f.appId} onChange={(e) => set('appId', e.target.value)} autoComplete="off" data-testid="sb-appid" />
            </Field>
            {secret('apiKey', 'API key', 'sb-apikey')}
            {secret('secretKey', 'Secret key', 'sb-secret')}
          </div>
          <div className="space-y-3">
            {secret('webhookSecret', 'Webhook secret', 'sb-webhook')}
            <Field label="Environment">
              <Segmented ariaLabel="Environment" value={f.environment}
                options={[{ id: 'sandbox', label: 'Sandbox' }, { id: 'production', label: 'Production' }]}
                onChange={(v) => { setF((x) => ({ ...x, environment: v as 'sandbox' | 'production' })); setHostToConfirm(null); }} />
            </Field>
            <Field label="Base URL" error={show('baseUrl')} hint={`Default ${DEFAULT_SUNBAY_URL}`}>
              <input className={`${INPUT} font-mono`} value={f.baseUrl} onChange={(e) => set('baseUrl', e.target.value)} placeholder="https://…" data-testid="sb-url" />
            </Field>
            {hostToConfirm && (
              <div role="alert" data-testid="sb-host-confirm" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {hostToConfirm} is not a sunbay.us host.
                <button type="button" onClick={() => submit(true)} disabled={busy}
                  className="ml-2 min-h-[44px] rounded-md px-2 font-semibold text-ruby hover:bg-[#FFF1F3]">Use it anyway</button>
              </div>
            )}
            {error && <p role="alert" className="text-[12px] text-ruby-deep">{error}</p>}
          </div>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-stone-600 hover:bg-stone-100">Cancel</button>
          <button type="button" data-testid="sb-save" disabled={Object.keys(problems).length > 0 || busy} onClick={() => submit(false)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-5 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
            {busy && <RubyGemMark size={16} drawing color="currentColor" />} Save
          </button>
        </footer>
      </div>
    </div>
  );
};
